import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Branding, Copyright } from './Layout';

const API_BASE = process.env.REACT_APP_API_URL || '/speeddate/api';
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'AeMlnmkBe2UhQAi_9v6xw-zCko_C8tPi6OLl4d81eRh7nTskVR2zQveC3CIxENZ9Lal3IrnxJyhwJPRM';
// SOLA-140: costo del servizio fisso PayPal (allineato a PAYPAL_SERVICE_FEE in
// packages/payment-core/src/fees.ts e backend/src/routes.js). L'addebito
// avviene lato backend nel create-order: qui serve solo per il riepilogo.
const PAYPAL_SERVICE_FEE = 1.00;

const HowItWorksSection = ({ templateKey }) => {
  const [content, setContent] = useState(null);
  useEffect(() => {
    if (!templateKey) return;
    axios.get(`/speeddate/api/templates/${templateKey}`).then(r => setContent(r.data.content)).catch(() => {});
  }, [templateKey]);
  if (!content) return null;
  return (
    <div className="bg-light p-4 rounded-4">
      <p className="fw-700 text-dark mb-3 small text-uppercase ls-sm"><i className="fas fa-info-circle me-2 text-primary"></i>Come funziona</p>
      <div className="text-dark fw-600 small" style={{ lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br>') }} />
    </div>
  );
};

const EventRegistration = ({ event, onBack, participant, onRegistered }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cart, setCart] = useState({}); // { ticketId: quantity }
    const [otherParticipants, setOtherParticipants] = useState([]); // Array of { firstName, lastName, email, sex, age }
    const [emailExistsCache, setEmailExistsCache] = useState({});
    const [showPayPal, setShowPayPal] = useState(true);
    const [addedParticipants, setAddedParticipants] = useState([]);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null); // null | 'paypal' | 'card'
    // SOLA-135: flag per bloccare l'acquisto quando le restrizioni di genere impediscono
    // all'utente corrente di acquistare biglietti (es. uomo su evento ultimiPostiDonna).
    const [genderBlocked, setGenderBlocked] = useState(false);
    const [genderBlockReason, setGenderBlockReason] = useState('');

    const isAlreadyRegistered = Boolean(
        event.isRegistered ||
        (participant.EventId && Number(participant.EventId) === Number(event.id)) ||
        (participant.Participations && participant.Participations.some(p => Number(p.EventId) === Number(event.id))) ||
        (participant.participations && participant.participations.some(p => Number(p.EventId) === Number(event.id)))
    );

    useEffect(() => {
        if (isAlreadyRegistered && participant.UserId) {
            axios.get(`/speeddate/api/participants/added-by/${participant.UserId}?eventId=${event.id}&excludeParticipantId=${participant.id}`)
                .then(res => setAddedParticipants(res.data))
                .catch(err => console.error(err));
        }
        fetchTickets();
    }, [event.id]);

    const fetchTickets = async () => {
        try {
            const res = await axios.get(`/speeddate/api/tickets?eventId=${event.id}`);
            const activeTickets = res.data.filter(t => t.active);
            
            // Se NON è già registrato a questo evento, filtra per sesso
            const alreadyRegistered = Boolean(
                event.isRegistered ||
                (participant.EventId && Number(participant.EventId) === Number(event.id)) ||
                (participant.Participations && participant.Participations.some(p => Number(p.EventId) === Number(event.id))) ||
                (participant.participations && participant.participations.some(p => Number(p.EventId) === Number(event.id)))
            );
            
            // SOLA-126: rileggi l'evento dal server: una SPA/PWA aperta da ore
            // mostrerebbe ancora i flag di restrizione vecchi.
            let liveEvent = event;
            try {
                const liveRes = await axios.get(`/speeddate/api/events/${event.id}`);
                liveEvent = { ...event, ...liveRes.data };
            } catch (_) { /* si prosegue con i dati disponibili */ }

            // SOLA-135: controllo restrizioni di genere sull'evento
            if (!alreadyRegistered) {
                const isMale = participant.sex === 'M' || participant.sex === 'male';
                if (liveEvent.ultimiPostiDonna && isMale) {
                    setGenderBlocked(true);
                    setGenderBlockReason('Ultimi posti disponibili solo per donne. Non puoi acquistare biglietti per questo evento.');
                    setTickets([]);
                    setLoading(false);
                    return;
                }
                if (liveEvent.ultimiPostiUomo && !isMale) {
                    setGenderBlocked(true);
                    setGenderBlockReason('Ultimi posti disponibili solo per uomini. Non puoi acquistare biglietti per questo evento.');
                    setTickets([]);
                    setLoading(false);
                    return;
                }
                setTickets(activeTickets.filter(t => isMale ? t.maleCount > 0 : t.femaleCount > 0));
            } else {
                // Già registrato: può aggiungere qualsiasi biglietto (amici/coppie)
                setTickets(activeTickets);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const updateQuantity = (id, delta) => {
        setCart(prev => {
            const current = prev[id] || 0;
            const next = Math.max(0, current + delta);
            return { ...prev, [id]: next };
        });
        // PayPal always shown
    };

    // ── Calcoli e buildItems: funzioni inline ──
    const isMale = participant.sex === 'M' || participant.sex === 'male';
    const isFemale = participant.sex === 'F' || participant.sex === 'female';

    const _calcTotal = (tks, c) => tks.reduce((s, t) => s + (t.price * (c[t.id] || 0)), 0);
    const _calcTotalItems = (tks, c) => Object.values(c).reduce((a, b) => a + b, 0);
    const _calcTotalMales = (tks, c) => tks.reduce((s, t) => s + (!t.optional ? t.maleCount * (c[t.id] || 0) : 0), 0);
    const _calcTotalFemales = (tks, c) => tks.reduce((s, t) => s + (!t.optional ? t.femaleCount * (c[t.id] || 0) : 0), 0);
    const _hasNonOptional = (tks, c) => tks.some(t => !t.optional && (c[t.id] || 0) > 0);
    const _hasOwnSex = (guestMode, alreadyReg, male, tM, tF) => alreadyReg || (male && tM >= 1) || (!male && tF >= 1);
    const _calcReqMales = (guestMode, male, alreadyReg, tM) => Math.max(0, tM - (male && !alreadyReg ? 1 : 0));
    const _calcReqFemales = (guestMode, male, alreadyReg, tF) => Math.max(0, tF - (!male && !alreadyReg ? 1 : 0));

    const total = _calcTotal(tickets, cart);
    const paypalServiceFee = total > 0 ? PAYPAL_SERVICE_FEE : 0;
    const paypalTotal = Math.round((total + paypalServiceFee) * 100) / 100;
    const totalItems = _calcTotalItems(tickets, cart);
    const hasTicketsSelected = totalItems > 0;
    const totalMales = _calcTotalMales(tickets, cart);
    const totalFemales = _calcTotalFemales(tickets, cart);
    const hasNonOptional = _hasNonOptional(tickets, cart);
    const hasOwnSex = _hasOwnSex(false, isAlreadyRegistered, isMale, totalMales, totalFemales);
    const reqMales = _calcReqMales(false, isMale, isAlreadyRegistered, totalMales);
    const reqFemales = _calcReqFemales(false, isMale, isAlreadyRegistered, totalFemales);
    const totalOtherNeeded = reqMales + reqFemales;

    // Reset payment method when cart empties (deve stare DOPO le dichiarazioni
    // const per evitare temporal dead zone — JavaScript non hoists const)
    useEffect(() => {
        if (totalItems === 0) setPaymentMethod(null);
    }, [totalItems]);

    // Sync otherParticipants array when cart changes
    useEffect(() => {
        const currentList = [...otherParticipants];
        const newList = [];

        for (let i = 0; i < reqMales; i++) {
            const existing = currentList.find((p, idx) => p.sex === 'M' && !newList.some(n => n.originalIdx === idx));
            newList.push(existing ? { ...existing, originalIdx: currentList.indexOf(existing) } : { firstName: '', lastName: '', email: '', sex: 'M', age: '', phone: '' });
        }
        for (let i = 0; i < reqFemales; i++) {
            const existing = currentList.find((p, idx) => p.sex === 'F' && !newList.some(n => n.originalIdx === idx));
            newList.push(existing ? { ...existing, originalIdx: currentList.indexOf(existing) } : { firstName: '', lastName: '', email: '', sex: 'F', age: '', phone: '' });
        }

        setOtherParticipants(newList.map(({ originalIdx, ...rest }) => rest));
    }, [reqMales, reqFemales]);

    const updateOtherParticipant = (idx, field, value) => {
        const newList = [...otherParticipants];
        newList[idx][field] = value;
        setOtherParticipants(newList);
    };

    const handleEmailBlur = async (email) => {
        if (!email) return;
        const normalized = email.toLowerCase().trim();
        if (emailExistsCache[normalized] !== undefined) return;
        try {
            const res = await axios.get(`/speeddate/api/users/check-email?email=${encodeURIComponent(normalized)}`);
            setEmailExistsCache(prev => ({ ...prev, [normalized]: res.data.exists }));
        } catch (err) {
            console.error(err);
        }
    };

    // Email Uniqueness Check
    const allEmails = [participant.email, ...otherParticipants.map(p => p.email)].filter(e => !!e);
    const hasDuplicateEmails = new Set(allEmails).size !== allEmails.length;

    const isFormValid = totalItems > 0 &&
        hasOwnSex &&
        hasNonOptional &&
        !hasDuplicateEmails &&
        privacyAccepted &&
        // SOLA-126: ogni posto non opzionale (escluso il proprio) deve avere un
        // partecipante compilato, altrimenti il biglietto pagato resterebbe
        // senza partecipante (o finirebbe assegnato all'acquirente per errore).
        otherParticipants.length >= totalOtherNeeded &&
        // Tutti i partecipanti aggiuntivi devono avere nome, email e telefono
        // (l'eta non e piu richiesta - rimossa su richiesta utente)
        otherParticipants.every(p => p.firstName && p.email && p.phone && String(p.phone).trim());

    // Card payment handler
    const handleCardPayment = async () => {
        if (!isFormValid || total === 0) return;
        setIsProcessing(true);
        try {
            const sessionToken = localStorage.getItem('www2_sessionToken');
            const headers = { 'Content-Type': 'application/json' };
            if (sessionToken) headers['x-session-token'] = sessionToken;
            headers['x-sola-channel'] = 'app'; // SOLA-121: acquisti da app.solteros.it

            const res = await axios.post(API_BASE + '/payments/igfs/init', {
                amount: total.toFixed(2),
                eventId: event.id,
                currency: 'EUR',
                description: 'Speed Date: acquisto biglietti',
                items: getItems(),
                otherParticipants,
                guestMode: false,
                userEmail: participant.email || undefined,
                participantId: participant.UserId || participant.id,
                couponCode: null,
                couponValue: null,
            }, { headers });

            if (res.data.redirectUrl) {
                window.location.href = res.data.redirectUrl;
            } else {
                throw new Error('URL di pagamento non ricevuto');
            }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Errore nel pagamento con carta';
            window.Swal.fire('Errore', msg, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    // PayPal handlers
    const createOrder = async () => {
        const res = await axios.post('/speeddate/api/payments/paypal/create-order', {
            amount: total.toFixed(2),
            currency: 'EUR',
            description: `Speed Date: ${event.name}`
        }, { headers: { 'x-sola-channel': 'app' } }); // SOLA-121: acquisti da app.solteros.it
        return res.data.id;
    };

    // ── buildItems: dal package condiviso o fallback minimo ──
    function getItems() {
      const items = [];
      let selfAssigned = isAlreadyRegistered;
      const maleOthers = otherParticipants.map((p, idx) => ({ ...p, idx })).filter(p => p.sex === 'M' || p.sex === 'male');
      const femaleOthers = otherParticipants.map((p, idx) => ({ ...p, idx })).filter(p => p.sex === 'F' || p.sex === 'female');
      let mi = 0, fi = 0;
      tickets.forEach(t => {
        const qty = cart[t.id] || 0;
        const maleSeats = t.maleCount || 0;
        const femaleSeats = t.femaleCount || 0;
        const totalSeats = maleSeats + femaleSeats || 1;
        const pricePerSeat = parseFloat((parseFloat(t.price) / totalSeats).toFixed(2));
        for (let u = 0; u < qty * totalSeats; u++) {
          const seatSex = (u % totalSeats) < maleSeats ? 'male' : 'female';
          let person;
          if (t.optional) { person = 'self'; }
          else if (maleSeats === 0 && femaleSeats === 0) { person = 'self'; }
          else if (seatSex === 'male') {
            if (isMale && !selfAssigned) { person = 'self'; selfAssigned = true; }
            else if (maleOthers[mi]) { person = maleOthers[mi].idx; mi++; }
            else person = 'self';
          } else {
            if (!isMale && !selfAssigned) { person = 'self'; selfAssigned = true; }
            else if (femaleOthers[fi]) { person = femaleOthers[fi].idx; fi++; }
            else person = 'self';
          }
          items.push({ ticketId: t.id, name: t.name, price: pricePerSeat, optional: !!t.optional, sex: maleSeats === 0 && femaleSeats === 0 ? undefined : seatSex, person });
        }
      });
      return items;
    }

    const onApprove = async (data) => {
        try {
            setIsProcessing(true);
            const sessionToken = localStorage.getItem('www2_sessionToken');
            const headers = { 'Content-Type': 'application/json' };
            if (sessionToken) headers['x-session-token'] = sessionToken;
            headers['x-sola-channel'] = 'app'; // SOLA-121: acquisti da app.solteros.it
            // SOLA-126: `participant` può essere una riga Participants oppure un
            // Users (login via token). Inviamo id espliciti per evitare che un
            // Participants.id venga interpretato come Users.id e che l'acquisto
            // resti senza partecipante.
            const isParticipantRow = Boolean(participant.EventId);
            const buyerUserId = participant.UserId
                || (isParticipantRow ? null : participant.id)
                || null;
            const res = await axios.post(API_BASE + '/payments/paypal/capture-order', {
                orderId: data.orderID,
                eventId: event.id,
                otherParticipants,
                items: getItems(),
                userId: buyerUserId || undefined,
                participantId: isParticipantRow ? participant.id : undefined,
                email: participant.email || undefined
            }, { headers });

            if (res.data.success) {
                // Meta Pixel: Purchase event
                try {
                    window.fbq('track', 'Purchase', {
                        value: paypalTotal,
                        currency: 'EUR',
                        content_ids: [String(event.id)],
                        content_type: 'product',
                    });
                } catch (e) { /* pixel not loaded */ }

                await window.Swal.fire({
                    title: isAlreadyRegistered ? 'Amici Aggiunti!' : 'Pagamento Completato!',
                    text: isAlreadyRegistered
                        ? 'I tuoi amici sono stati registrati con successo.'
                        : 'Il pagamento è andato a buon fine. Ti aspettiamo all\'evento!',
                    icon: 'success',
                    confirmButtonColor: '#db2777',
                    timer: 3000,
                    showConfirmButton: false
                });
                if (onRegistered) onRegistered(res.data.participant);
                window.location.href = '/speeddate/';
            }
        } catch (err) {
            const msg = err.response?.data?.error || 'Si è verificato un problema durante il pagamento.';
            window.Swal.fire('Errore', msg, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const onError = (err) => {
        console.error('PayPal error:', err);
        window.Swal.fire('Errore PayPal', 'Si è verificato un problema con PayPal. Riprova.', 'error');
    };

    // Free events (total == 0): register directly without PayPal
    const handleFreeRegistration = async () => {
        if (!isFormValid) return;
        try {
            setIsProcessing(true);
            const isParticipantRowFree = Boolean(participant.EventId);
            const freeUserId = participant.UserId
                || (isParticipantRowFree ? null : participant.id)
                || null;
            const res = await axios.post('/speeddate/api/users/register-to-event', {
                eventId: event.id,
                userId: freeUserId || undefined,
                participantId: isParticipantRowFree ? participant.id : undefined,
                email: participant.email || undefined,
                otherParticipants,
                items: getItems()
            });
            if (res.data.success) {
                // Meta Pixel: Lead event for free registration
                try {
                    window.fbq('track', 'Lead', { currency: 'EUR', value: 0 });
                } catch (e) { /* pixel not loaded */ }

                await window.Swal.fire({
                    title: isAlreadyRegistered ? 'Amici Aggiunti!' : 'Iscrizione Completata!',
                    text: 'Sei stato registrato con successo!',
                    icon: 'success',
                    confirmButtonColor: '#db2777'
                });
                if (onRegistered) onRegistered(res.data.participant);
            }
        } catch (err) {
            const msg = err.response?.data?.error || 'Si è verificato un problema durante l\'iscrizione.';
            window.Swal.fire('Errore', msg, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="mx-auto w-100 fade-in" style={{ maxWidth: '800px' }}>
            <div className="card glass-premium border-0 p-0 mb-4 shadow-lg overflow-hidden" style={{ borderRadius: '45px' }}>
                {/* Header */}
                <div className="p-4 p-md-5 bg-white border-bottom position-relative">
                    <button
                        className="btn btn-light rounded-circle shadow-sm position-absolute"
                        style={{ top: '25px', left: '25px', width: '45px', height: '45px', zIndex: 10 }}
                        onClick={onBack}
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>

                    <div className="text-center mt-3">
                        <Branding />
                        {event.coverImage && (
                            <div className="mb-4 rounded-4 overflow-hidden shadow-sm" style={{ maxHeight: '300px' }}>
                                <img
                                    src={event.coverImage}
                                    alt={event.name}
                                    className="w-100"
                                    style={{ objectFit: 'cover', maxHeight: '300px' }}
                                />
                            </div>
                        )}
                        <h1 className="fw-900 text-dark mt-4 mb-2" style={{ fontSize: '2.5rem' }}>{event.name}</h1>
                        <div className="d-flex justify-content-center gap-3 text-pink-600 fw-700 small text-uppercase ls-md">
                            <span><i className="fas fa-calendar-day me-2"></i>{new Date(event.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            <span><i className="fas fa-clock me-2"></i>{event.startTime?.substring(0, 5)} - {event.endTime?.substring(0, 5)}</span>
                        </div>
                        {event.soldOut ? (
                            <div className="badge text-white p-2 px-4 rounded-pill fw-900 fs-6 shadow-sm mt-3 animate-pulse" style={{ background: 'linear-gradient(135deg, #be123c, #dc2626)' }}>
                                <i className="fas fa-ban me-2"></i> SOLD OUT
                            </div>
                        ) : event.ultimiPostiDonna ? (
                            <div className="badge text-white p-2 px-4 rounded-pill fw-900 fs-6 shadow-sm mt-3" style={{ background: 'linear-gradient(135deg, #c026d3, #e879f9)' }}>
                                <i className="fas fa-venus me-2"></i> ULTIMI POSTI DONNA
                            </div>
                        ) : event.ultimiPostiUomo ? (
                            <div className="badge text-white p-2 px-4 rounded-pill fw-900 fs-6 shadow-sm mt-3" style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)' }}>
                                <i className="fas fa-mars me-2"></i> ULTIMI POSTI UOMO
                            </div>
                        ) : null}
                        {isAlreadyRegistered && (
                            <div className="badge bg-success text-white p-2 px-4 rounded-pill fw-900 fs-6 shadow-sm mt-3 animate-pulse">
                                <i className="fas fa-check-circle me-2"></i> SEI ISCRITTO
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 p-md-5">
                    <div className="row g-5">
                        {/* Event Info */}
                        <div className="col-lg-7">
                            <h4 className="fw-900 text-dark mb-4 border-bottom pb-2">Informazioni Evento</h4>
                            <div className="mb-4">
                                <p className="fw-700 text-muted mb-1 small text-uppercase ls-sm">Location</p>
                                <p className="fw-900 text-dark h5 mb-3"><i className="fas fa-map-marker-alt text-pink-600 me-2"></i>{event.location} - {event.city?.name}</p>
                                {isAlreadyRegistered && (
                                    <div className="badge bg-success text-white p-2 px-3 rounded-pill fw-900 fs-6 shadow-sm mt-1">
                                        <i className="fas fa-check-circle me-2"></i> TICKET PERSONALE ACQUISTATO
                                    </div>
                                )}
                            </div>
                            {event.description && event.description.trim() && (
                            <div className="event-description-container bg-light p-4 rounded-4">
                                <p className="fw-700 text-muted mb-3 small text-uppercase ls-sm">Descrizione</p>
                                <div
                                    className="text-dark fw-600 small"
                                    style={{ lineHeight: '1.6' }}
                                    dangerouslySetInnerHTML={{ __html: event.description.trim().replace(/\n/g, '<br>') }}
                                />
                            </div>
                            )}

                            {/* Tags */}
                            {event.tags && event.tags.length > 0 && (
                              <div className="mt-3 d-flex flex-wrap gap-2">
                                {event.tags.map(t => (
                                  <span key={t.id}
                                    className={`badge rounded-pill px-3 py-1 fw-600 ${t.type === 'etn_category' ? 'bg-purple-soft text-purple' : 'bg-amber-soft text-amber'}`}
                                    style={{
                                      background: t.type === 'etn_category' ? 'rgba(139,92,246,0.12)' : 'rgba(245,158,11,0.12)',
                                      color: t.type === 'etn_category' ? '#6d28d9' : '#b45309',
                                      border: '1px solid ' + (t.type === 'etn_category' ? 'rgba(139,92,246,0.25)' : 'rgba(245,158,11,0.25)'),
                                    }}
                                  >{t.name}</span>
                                ))}
                              </div>
                            )}

                            {/* Come funziona */}
                            {event.howItWorksTemplateKey && (
                              <div className="mt-4">
                                <HowItWorksSection templateKey={event.howItWorksTemplateKey} />
                              </div>
                            )}

                            {/* Price includes/excludes */}
                            {(event.priceIncludes || event.priceExcludes) && (
                              <div className="mt-4 p-4 bg-light rounded-4">
                                {event.priceIncludes && (
                                  <>
                                    <p className="fw-700 text-success mb-2 small text-uppercase ls-sm"><i className="fas fa-check-circle me-2"></i>La quota comprende</p>
                                    <div className="text-dark fw-600 small" style={{ lineHeight: '1.6' }}
                                      dangerouslySetInnerHTML={{ __html: event.priceIncludes.replace(/\n/g, '<br>') }} />
                                  </>
                                )}
                                {event.priceExcludes && (
                                  <>
                                    <p className="fw-700 text-danger mb-2 mt-3 small text-uppercase ls-sm"><i className="fas fa-times-circle me-2"></i>La quota non comprende</p>
                                    <div className="text-dark fw-600 small" style={{ lineHeight: '1.6' }}
                                      dangerouslySetInnerHTML={{ __html: event.priceExcludes.replace(/\n/g, '<br>') }} />
                                  </>
                                )}
                              </div>
                            )}
                        </div>

                        {/* Checkout & PayPal */}
                        <div className="col-lg-5">
                            {isAlreadyRegistered && !event.soldOut && (
                                <div className="card bg-white border-0 shadow-sm rounded-5 overflow-hidden mb-4">
                                    <div className="bg-success p-4 text-white text-center">
                                        <h5 className="fw-900 mb-0 text-uppercase ls-lg"><i className="fas fa-check-circle me-2"></i>Sei Iscritto!</h5>
                                    </div>
                                    <div className="p-4">
                                        <div className="bg-light p-4 rounded-4 mb-4">
                                            <p className="fw-900 text-dark mb-1 h6">{participant.firstName} {participant.lastName}</p>
                                            <p className="text-muted small mb-0">{participant.email}</p>
                                            <div className="mt-2">
                                                <span className={`badge ${isMale ? 'bg-primary' : 'bg-pink-600'} rounded-pill small`}>
                                                    {isMale ? 'Uomo' : 'Donna'}
                                                </span>
                                            </div>
                                        </div>

                                        {addedParticipants.length > 0 && (
                                            <div className="border-top pt-4">
                                                <h6 className="fw-900 text-dark mb-3 text-uppercase ls-sm">
                                                    <i className="fas fa-users me-2 text-muted"></i>Altri Partecipanti
                                                </h6>
                                                {addedParticipants.map((p, idx) => (
                                                    <div key={idx} className="bg-light p-3 rounded-4 mb-3 border-start border-4" style={{ borderColor: p.sex === 'male' ? '#3b82f6' : '#db2777' }}>
                                                        <p className="fw-900 small mb-1 text-dark">{p.firstName} {p.lastName}</p>
                                                        <p className="text-muted small mb-0">{p.email}</p>
                                                        <span className={`badge mt-2 ${p.sex === 'male' ? 'bg-primary-subtle text-primary' : 'bg-danger-subtle text-danger'} rounded-pill`} style={{ fontSize: '10px' }}>
                                                            {p.sex === 'male' ? 'Uomo' : 'Donna'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {event.soldOut ? (
                                <div className="card bg-white border-0 shadow-sm rounded-5 overflow-hidden mt-4" style={{ border: '2px solid rgba(190,18,60,0.4)' }}>
                                    <div className="p-4 text-center" style={{ background: 'linear-gradient(135deg, rgba(190,18,60,0.08), rgba(220,38,38,0.15))' }}>
                                        <div style={{ fontSize: '3rem', marginBottom: '8px' }}>🚫</div>
                                        <h5 className="fw-900 mb-1" style={{ color: '#be123c' }}>Evento SOLD OUT</h5>
                                        <p className="text-muted small mb-0">Tutti i posti per questo evento sono esauriti. L'acquisto dei biglietti è stato sospeso dall'organizzatore.</p>
                                    </div>
                                </div>
                            ) : genderBlocked ? (
                                <div className="card bg-white border-0 shadow-sm rounded-5 overflow-hidden mt-4" style={{ border: '2px solid rgba(190,18,60,0.4)' }}>
                                    <div className="p-4 text-center" style={{ background: 'linear-gradient(135deg, ' + (event.ultimiPostiDonna ? 'rgba(192,38,211,0.08)' : 'rgba(37,99,235,0.08)') + ', rgba(220,38,38,0.15))' }}>
                                        <div style={{ fontSize: '3rem', marginBottom: '8px' }}>{event.ultimiPostiDonna ? '👩' : '👨'}</div>
                                        <h5 className="fw-900 mb-1" style={{ color: '#be123c' }}>Ultimi posti — solo {event.ultimiPostiDonna ? 'donne' : 'uomini'}</h5>
                                        <p className="text-muted small mb-0">{genderBlockReason}</p>
                                    </div>
                                </div>
                            ) : (
                            <div className="card bg-white border-0 shadow-sm rounded-5 overflow-hidden mt-4">
                                <div className="bg-pink-600 p-4 text-white text-center">
                                    <h5 className="fw-900 mb-0 text-uppercase ls-lg">Il Tuo Carrello</h5>
                                </div>
                                <div className="p-4">
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-pink-600" role="status"></div>
                                        </div>
                                    ) : (
                                        <div className="ticket-list mb-4">
                                            {tickets.map(t => (
                                                <div key={t.id} className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light last-child-border-0">
                                                    <div className="flex-grow-1">
                                                        <p className="fw-900 text-dark mb-0">{t.name}{t.optional ? <span className="badge bg-warning text-dark rounded-pill ms-2" style={{ fontSize: '10px' }}>Opzionale</span> : null}</p>
                                                        <p className="text-pink-600 fw-700 small mb-0">€{parseFloat(t.price).toFixed(2)}</p>
                                                        <div className="mt-1 d-flex gap-1">
                                                            {t.maleCount > 0 && <span className="badge bg-primary-subtle text-primary rounded-pill" style={{ fontSize: '9px' }}>{t.maleCount}M</span>}
                                                            {t.femaleCount > 0 && <span className="badge bg-danger-subtle text-danger rounded-pill" style={{ fontSize: '9px' }}>{t.femaleCount}F</span>}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center bg-light rounded-pill p-1 shadow-inner">
                                                        <button
                                                            className="btn btn-sm btn-white rounded-circle shadow-sm"
                                                            style={{ width: '30px', height: '30px' }}
                                                            onClick={() => updateQuantity(t.id, -1)}
                                                        >
                                                            <i className="fas fa-minus small text-muted"></i>
                                                        </button>
                                                        <span className="px-3 fw-900 text-dark" style={{ minWidth: '40px', textAlign: 'center' }}>{cart[t.id] || 0}</span>
                                                        <button
                                                            className="btn btn-sm btn-white rounded-circle shadow-sm"
                                                            style={{ width: '30px', height: '30px' }}
                                                            onClick={() => updateQuantity(t.id, 1)}
                                                        >
                                                            <i className="fas fa-plus small text-muted"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Additional Participants */}
                                    {otherParticipants.length > 0 && (
                                        <div className="mt-4 pt-4 border-top">
                                            <h6 className="fw-900 text-dark mb-3 text-uppercase ls-sm">Dati Altri Invitati</h6>
                                            {otherParticipants.map((p, idx) => (
                                                <div key={idx} className="bg-light p-3 rounded-4 mb-3 border-start border-4" style={{ borderColor: p.sex === 'M' ? '#3b82f6' : '#db2777' }}>
                                                    <p className="fw-900 small mb-2 text-dark opacity-75">{p.sex === 'M' ? 'UOMO' : 'DONNA'} AGGIUNTIVO</p>
                                                    <div className="row g-2">
                                                        <div className="col-6">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Nome*"
                                                                value={p.firstName}
                                                                onChange={(e) => updateOtherParticipant(idx, 'firstName', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-6">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Cognome"
                                                                value={p.lastName}
                                                                onChange={(e) => updateOtherParticipant(idx, 'lastName', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-12">
                                                            <input
                                                                type="email"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Email*"
                                                                value={p.email}
                                                                onChange={(e) => updateOtherParticipant(idx, 'email', e.target.value)}
                                                                onBlur={() => handleEmailBlur(p.email)}
                                                            />
                                                        </div>
                                                        <div className="col-12 mt-1">
                                                            <input
                                                                type="tel"
                                                                className="form-control form-control-sm rounded-pill"
                                                                placeholder="Telefono*"
                                                                value={p.phone || ''}
                                                                onChange={(e) => updateOtherParticipant(idx, 'phone', e.target.value)}
                                                            />
                                                        </div>
                                                        {/* Campo eta rimosso su richiesta utente */}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Privacy consent checkbox */}
                                    <div className="form-check mb-3">
                                        <label className="form-check-label" style={{fontSize:'12px',color:'#6b7280',lineHeight:'1.5',cursor:'pointer'}}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={privacyAccepted}
                                                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                                                required
                                                style={{marginTop:'3px',accentColor:'#db2777'}}
                                            />
                                            I dati personali saranno utilizzati esclusivamente per le finalità degli eventi e non per finalità di marketing.
                                            Il numero di cellulare potrà essere utilizzato esclusivamente per comunicazioni urgenti relative all'evento, come variazioni improvvise o cambiamenti di programma.
                                            (<a href="https://www.iubenda.com/privacy-policy/14519431" target="_blank" rel="noopener noreferrer" style={{color:'#db2777'}}>Privacy Policy</a>)
                                        </label>
                                    </div>

                                    {/* Summary */}
                                    <div className="bg-light p-4 rounded-4 mt-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted fw-700 small">Riepilogo:</span>
                                            <div className="d-flex gap-2">
                                                <span className="badge bg-primary rounded-pill small">{totalMales}M</span>
                                                <span className="badge bg-pink-600 rounded-pill small">{totalFemales}F</span>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top border-2 border-white">
                                            <span className="h5 fw-900 text-dark mb-0">TOTALE:</span>
                                            <span className="h4 fw-900 text-pink-600 mb-0">€{total.toFixed(2)}</span>
                                        </div>
                                        <p className="text-muted mb-0 mt-2" style={{fontSize:'11px'}}>
                                            Con PayPal si aggiunge un costo del servizio fisso di €{PAYPAL_SERVICE_FEE.toFixed(2)} (totale €{paypalTotal.toFixed(2)}). Con carta/NEXI il servizio è gratuito.
                                        </p>
                                    </div>

                                    {hasDuplicateEmails && (
                                        <div className="alert alert-danger border-0 rounded-4 mt-3 small fw-700 py-2">
                                            <i className="fas fa-envelope me-2"></i>
                                            Email duplicate non ammesse.
                                        </div>
                                    )}

                                    {/* Payment method selector */}
                                    {total > 0 ? (
                                        !isFormValid ? (
                                            <div className="alert alert-warning border-0 rounded-4 mt-3 text-center fw-700 py-3">
                                                <i className="fas fa-lock me-2"></i>
                                                {!privacyAccepted && 'Devi accettare l\'informativa sulla privacy. '}
                                                {!hasOwnSex && 'Devi includere te stesso. '}
                                                {!hasNonOptional && 'Seleziona almeno un biglietto di ingresso. '}
                                                {hasDuplicateEmails && 'Email duplicate non ammesse. '}
                                                {otherParticipants.length > 0 && otherParticipants.some(p => !p.firstName || !p.email || !p.phone) && 'Compila tutti i campi obbligatori dei partecipanti aggiuntivi (nome, email, telefono). '}
                                            </div>
                                        ) : paymentMethod === null ? (
                                            <div className="mt-4">
                                                <p className="text-center text-muted small fw-700 mb-3">Scegli il metodo di pagamento</p>
                                                <button className="btn btn-primary w-100 rounded-pill py-3 mb-2 fw-900"
                                                    onClick={() => setPaymentMethod('paypal')}>
                                                    <i className="fab fa-paypal me-2"></i> PayPal — €{paypalTotal.toFixed(2)} (incl. €{PAYPAL_SERVICE_FEE.toFixed(2)} servizio)
                                                </button>
                                                <button className="btn btn-secondary w-100 rounded-pill py-3 mb-3 fw-900"
                                                    onClick={() => setPaymentMethod('card')}>
                                                    <i className="fas fa-credit-card me-2"></i> Carta di Credito/Debito — servizio gratuito
                                                </button>
                                            </div>
                                        ) : paymentMethod === 'paypal' ? (
                                        <div className="mt-4">
                                            <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setPaymentMethod(null)}>← Cambia metodo</button>
                                            <p className="text-center text-muted small fw-700 mb-1">
                                                <i className="fab fa-paypal me-2 text-primary"></i>
                                                Paga in modo sicuro con PayPal
                                            </p>
                                            <p className="text-center text-muted mb-3" style={{fontSize:'12px'}}>
                                                Biglietti €{total.toFixed(2)} + costo del servizio €{paypalServiceFee.toFixed(2)} = <strong>€{paypalTotal.toFixed(2)}</strong>
                                            </p>
                                            <PayPalScriptProvider options={{
                                                'client-id': PAYPAL_CLIENT_ID,
                                                currency: 'EUR',
                                                locale: 'it_IT',
                                                disableFunding: 'card,bancontact,blik,eps,ideal,mybank,p24,trustly,multibanco,sepa',
                                            }}>
                                                <PayPalButtons
                                                    style={{ layout: 'vertical', shape: 'pill', color: 'gold', label: 'paypal' }}
                                                    disabled={isProcessing || !isFormValid}
                                                    createOrder={createOrder}
                                                    onApprove={onApprove}
                                                    onError={onError}
                                                />
                                            </PayPalScriptProvider>
                                        </div>
                                        ) : (
                                        <div className="mt-4">
                                            <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setPaymentMethod(null)}>← Cambia metodo</button>
                                            <p className="text-center text-muted small fw-700 mb-3">
                                                <i className="fas fa-credit-card me-2 text-secondary"></i>
                                                Paga con Carta di Credito/Debito
                                            </p>
                                            <button
                                                className="btn btn-danger w-100 rounded-pill py-3 fw-900 shadow-lg border-0"
                                                style={{ background: 'linear-gradient(45deg, #db2777, #f43f5e)' }}
                                                disabled={isProcessing || !isFormValid}
                                                onClick={handleCardPayment}
                                            >
                                                {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : <>Paga €{total.toFixed(2)} con Carta <i className="fas fa-chevron-right ms-2"></i></>}
                                            </button>
                                            <p className="text-muted small mt-2" style={{fontSize:'11px'}}>
                                                <i className="fas fa-lock me-1"></i>
                                                Pagamento sicuro tramite Nexi / NumiaPOS — nessun costo del servizio
                                            </p>
                                        </div>
                                        )
                                    ) : hasTicketsSelected ? (
                                        <button
                                            className="btn btn-danger w-100 rounded-pill py-3 mt-4 fw-900 shadow-lg border-0 transition-all hover-scale"
                                            style={{ background: 'linear-gradient(45deg, #db2777, #f43f5e)' }}
                                            disabled={!isFormValid || isProcessing}
                                            onClick={handleFreeRegistration}
                                        >
                                            {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : <>ISCRIVITI GRATUITAMENTE <i className="fas fa-chevron-right ms-2"></i></>}
                                        </button>
                                    ) : (
                                        <div className="text-center mt-4">
                                            <div className="d-inline-block bg-danger bg-opacity-10 text-danger border border-danger rounded-4 px-4 py-3 small fw-600" style={{ whiteSpace: 'normal', maxWidth: '220px', lineHeight: '1.5' }}>
                                                <i className="fas fa-exclamation-circle me-2"></i>
                                                Seleziona almeno un biglietto<br />per procedere
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Copyright />
        </div>
    );
};

export default EventRegistration;
